import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { doc, getDoc, updateDoc, collection, query, where, getDocs, limit } from 'firebase/firestore';
import { db } from '../../firebase';
import { EditCartItems } from './partials/EditCartItems';
import { formatCurrency } from '../Help/helper';
import { useAuth } from '../Auth/Auth';
import { toast, ToastContainer } from 'react-toastify';
import PrintableQuotation from './partials/PrintableQuotation';
import './Utils/QuotationModalPrint.css';
import BreadCrumb from '../BreadCrumb';
import logger from '../../utils/logger';
import { isServiceOrLabour } from '../../utils/validation';



const EditQuotation = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [includeTax, setIncludeTax] = useState(false);
  const [taxOnServicesOnly, setTaxOnServicesOnly] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [showNewItemForm, setShowNewItemForm] = useState(false);
    const [totals, setTotals] = useState({
        subtotal: 0,
        taxAmount: 0,
        totalSale: 0,
        totalCost: 0
    });

  const [quotation, setQuotation] = useState({
    customerName: '',
    jobCardNo: '',
    comments: '',
    cartItems: [], // Ensure this is part of your quotation structure
    
  });
  const [newItem, setNewItem] = useState({
    type: '',
    description: '',
    quantity: 0,
    costPrice: 0,
    salePrice: 0
  });
  const  {user}  = useAuth(); 
  const currentUser = user.email;
  
  const pages = [
    { name: 'Finance', to: '/finance', component: Link, current: false },
    { name: 'Quotations Admin', to: '/finance/quotations', component: Link, current: false }, 
    { name: 'Edit Quotation Details', to: '/finance/edit-quotation/{id}', component: Link, current: true },   
  ];



  useEffect(() => {
    const fetchQuotation = async () => {
      const docRef = doc(db, 'quotations', id);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        setQuotation(data);
        if (data.includeTax !== undefined) setIncludeTax(data.includeTax);
        if (data.taxOnServicesOnly !== undefined) setTaxOnServicesOnly(data.taxOnServicesOnly);
      } else {
        logger.warn('No such quotation!');
      }
    };

    fetchQuotation();
  }, [id]);

  // Fetch VIN from vehicles collection if not in quotation
  useEffect(() => {
    const fetchVehicleData = async () => {
      if (quotation && quotation.jobCardNo && !quotation.vin) {
        try {
          // Query for job card using jobCardNo field
          const jobsRef = collection(db, 'jobs');
          const q = query(jobsRef, where('jobCardNo', '==', quotation.jobCardNo), limit(1));
          const jobSnapshot = await getDocs(q);

          if (!jobSnapshot.empty) {
            const jobData = jobSnapshot.docs[0].data();

            if (jobData.vehicleId) {
              // Fetch vehicle data using vehicleId
              const vehicleRef = doc(db, 'vehicles', jobData.vehicleId);
              const vehicleSnap = await getDoc(vehicleRef);

              if (vehicleSnap.exists()) {
                const vin = vehicleSnap.data().VIN || 'N/A';
                // Update the quotation state with VIN
                setQuotation(prev => ({ ...prev, vin: vin }));
              }
            }
          }
        } catch (error) {
          logger.error('Error fetching VIN:', error);
        }
      }
    };

    fetchVehicleData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quotation?.jobCardNo]);

  useEffect(() => {
    const subtotal = quotation.cartItems.reduce((acc, item) => acc + item.salePrice * item.quantity, 0);
    const totalCost = quotation.cartItems.reduce((acc, item) => acc + item.costPrice * item.quantity, 0);

    // Calculate services subtotal separately
    const servicesSubtotal = quotation.cartItems
      .filter(isServiceOrLabour)
      .reduce((acc, item) => acc + item.salePrice * item.quantity, 0);

    // Apply conditional VAT logic
    let taxAmount = 0;
    if (includeTax) {
      const taxRate = 0.075; // 7.5%
      if (taxOnServicesOnly) {
        // Apply VAT only to services
        taxAmount = servicesSubtotal * taxRate;
      } else {
        // Apply VAT to all items
        taxAmount = subtotal * taxRate;
      }
    }

    const totalSale = subtotal + taxAmount;

    setTotals({
        subtotal,
        taxAmount,
        totalSale,
        totalCost
    });
}, [quotation.cartItems, includeTax, taxOnServicesOnly]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setQuotation(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const finalizeQuotation = () => {
    handleUpdate();
  };
  

  const handleSave = async () => {
    // Perform Firestore update operations here
    try {
      const updatedQuotationData = {
        ...quotation,
        subtotal: totals.subtotal,
        totalCost: totals.totalCost,
        taxAmount: totals.taxAmount,
        totalSale: totals.totalSale,
        includeTax,
        taxOnServicesOnly,
        modifiedBy: currentUser || 'unknown',
        comments: quotation.comments || '',
        updatedAt: new Date()
      };
      await updateDoc(doc(db, 'quotations', id), updatedQuotationData);
      toast.success('Quotation updated successfully');
      setIsSaved(true);
    } catch (error) {
      logger.error('Error updating quotation: ', error);
      toast.error('Error updating quotation.');
    }
  };

  const handleQuantityChange = (index, newQuantity) => {
    const updatedCartItems = quotation.cartItems.map((item, idx) => {
      if (idx === index) {
        return { ...item, quantity: parseInt(newQuantity, 10) };
      }
      return item;
    });
  
    setQuotation({ ...quotation, cartItems: updatedCartItems });
  };

  const handleCostPriceChange = (index, newCostPrice) => {
    const updatedCartItems = quotation.cartItems.map((item, idx) => {
      if (idx === index) {
        return { ...item, costPrice: parseFloat(newCostPrice) };
      }
      return item;
    });
  
    setQuotation({ ...quotation, cartItems: updatedCartItems });
  };

  const handleSalePriceChange = (index, newSalePrice) => {
    const updatedCartItems = quotation.cartItems.map((item, idx) => {
        if (idx === index) {
            return { ...item, salePrice: parseFloat(newSalePrice) };
        }
        return item;
    });

    setQuotation({ ...quotation, cartItems: updatedCartItems });
};


  const handleTypeChange = (index, newType) => {
    const updatedCartItems = quotation.cartItems.map((item, idx) => {
      if (idx === index) {
        return { ...item, type: newType };
      }
      return item;
    });
  
    setQuotation({ ...quotation, cartItems: updatedCartItems });
  };

  const handleRemoveFromCart = (index) => {
    const updatedCartItems = quotation.cartItems.filter((_, idx) => idx !== index);
    setQuotation({ ...quotation, cartItems: updatedCartItems });
  };

  const handleAddToCart = (item) => {
    if (item.description && item.quantity && item.costPrice && item.salePrice) {
      setQuotation(prevQuotation => ({
        ...prevQuotation,
        cartItems: [...prevQuotation.cartItems, item]
      }));
      setShowNewItemForm(false); // Hide the form
      setNewItem({ type: '', description: '', quantity: 0, costPrice: 0, salePrice: 0 }); // Reset form
    } else {
      toast.error("Please fill all fields correctly.");
    }
  };
  
  

  const handleUpdate = async () => {
    const updatedQuotationData = {
      ...quotation,
      subtotal: totals.subtotal,
      totalCost: totals.totalCost,
      taxAmount: totals.taxAmount,
      totalSale: totals.totalSale,
      includeTax,
      taxOnServicesOnly,
      modifiedBy: currentUser || 'unknown',
      comments: quotation.comments || '',
      updatedAt: new Date()
    };
  
    try {
      await updateDoc(doc(db, 'quotations', id), updatedQuotationData);
      toast.success('Quotation updated successfully');
      setTimeout(() => {
        navigate('/finance/quotations');
      }, 1000); 
    } catch (error) {
      logger.error('Error updating quotation: ', error);
      toast.error('Error updating quotation.');
    }
  };
  
  
 
  
  
  const handleCommentChange = (e) => {
    setQuotation({ ...quotation, comments: e.target.value });
  };

 

  const handleDescriptionChange = (index, newDescription) => {
    
    const updatedCartItems = quotation.cartItems.map((item, idx) => {
      if (idx === index) {
        return { ...item, description: newDescription };
      }
      return item;
    });
  
    setQuotation({ ...quotation, cartItems: updatedCartItems });
  };
  
  const handlePrintPreview = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  if (!quotation) return null; // Display loading or placeholder

  logger.debug("Updated cartItems:", quotation.cartItems);


  return (
    <>
    <BreadCrumb pages={pages} />
    <div className="p-6 bg-white rounded shadow-md">
      <h2 className="text-2xl font-bold mb-4">Edit Quotation</h2>
      <button
        type="button"
        className="mb-4 text-sm bg-indigo-500 hover:bg-indigo-600 text-white py-2 px-4 rounded"
        onClick={() => setShowNewItemForm(!showNewItemForm)}
      >
        {showNewItemForm ? 'Cancel Adding' : 'Add New Item'}
      </button>

     

      <form onSubmit={handleUpdate}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label htmlFor="customerName" className="block text-sm font-medium text-gray-700">Customer Name</label>
            <input
              type="text"
              name="customerName"
              id="customerName"
              value={quotation.customerName}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
            />
          </div>
          <div>
            <label htmlFor="jobCardNo" className="block text-sm font-medium text-gray-700">Job Card No</label>
            <input
              type="text"
              name="jobCardNo"
              id="jobCardNo"
              value={quotation.jobCardNo}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
            />
          </div>

          {/* Vehicle Information - Read Only */}
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">Vehicle Make</label>
            <p className="text-gray-600">{quotation.vehicleMake || 'N/A'}</p>
          </div>

          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">Vehicle Model</label>
            <p className="text-gray-600">{quotation.vehicleModel || 'N/A'}</p>
          </div>

          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">Vehicle Year</label>
            <p className="text-gray-600">{quotation.vehicleYear || 'N/A'}</p>
          </div>

          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">Registration Number</label>
            <p className="text-gray-600">{quotation.registrationNumber || 'N/A'}</p>
          </div>

          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">Driver Name</label>
            <p className="text-gray-600">{quotation.driverName || 'N/A'}</p>
          </div>

          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">Driver Phone Number</label>
            <p className="text-gray-600">{quotation.driverNumber || 'N/A'}</p>
          </div>

          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">Entry Mileage</label>
            <p className="text-gray-600">{quotation.entryMileage || 'N/A'}</p>
          </div>

          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">VIN Number</label>
            <p className="text-gray-600">{quotation.vin || 'N/A'}</p>
          </div>

          {/* Include other fields as needed */}
        </div>
  

                 



        <EditCartItems
         cartItems={quotation.cartItems}
         handleQuantityChange={handleQuantityChange}
         handleCostPriceChange={handleCostPriceChange}
         handleSalePriceChange={handleSalePriceChange}
         handleTypeChange={handleTypeChange}
         handleRemoveFromCart={handleRemoveFromCart}
         handleDescriptionChange={handleDescriptionChange}
         showNewItemForm={showNewItemForm}
         setShowNewItemForm={setShowNewItemForm}
         newItem={newItem}
         setNewItem={setNewItem}
         handleAddToCart={handleAddToCart}
        />
        
  



        <div className="mt-4">
            <label htmlFor="comments" className="block text-sm font-medium text-gray-700">Comments</label>
            <textarea
                id="comments"
                name="comments"
                rows="4"
                value={quotation.comments}
                onChange={handleCommentChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                placeholder="Add any comments about this edit..."
            ></textarea>
            </div>
       
      </form>

     
      <div className="flex">
      <div className="sticky flex-1 top-4 p-4 bg-gray-50 rounded shadow">
        <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-800">Totals</h2>
            <button onClick={finalizeQuotation} className="bg-blue-600 hover:bg-green-700 text-white text-sm py-2 px-4 rounded transition duration-200">
                Finalize Quotation
            </button>
        </div>
        <div className="mb-4">
            <label className="flex items-center space-x-3 mb-2">
                <input
                    type="checkbox"
                    checked={includeTax}
                    onChange={(e) => setIncludeTax(e.target.checked)}
                    className="form-checkbox h-5 w-5 text-blue-600"
                />
                <span className="text-sm text-gray-700">Include 7.5% Tax</span>
            </label>
            <div className="text-sm print:hidden">
                <p>SUBTOTAL: {formatCurrency(totals.subtotal)}</p>
                <p>VAT (7.5%): {includeTax ? formatCurrency(totals.taxAmount) : "₦0.00"}</p>
                <p className="font-bold text-xl">TOTAL: {formatCurrency(totals.totalSale)}</p>
            </div>
        </div>
    </div>

    {/* Margin Analysis */}
    <div className="p-4 bg-gray-50 rounded shadow flex-1 ml-4 print:hidden">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Margin Analysis</h2>
        <div className="text-sm">
            <p>Total Sale Amount: {formatCurrency(totals.totalSale)}</p>
            <p>Total Cost Amount: {formatCurrency(totals.totalCost)}</p>
            <p>Margin: {formatCurrency(totals.totalSale - totals.totalCost)}</p>
        </div>
    </div>


</div>

{/* VAT Controls */}
<div className="mt-6 p-4 bg-gray-50 rounded">
  <div className="mb-4">
    <label className="flex items-center">
      <input
        type="checkbox"
        checked={includeTax}
        onChange={(e) => setIncludeTax(e.target.checked)}
        className="mr-2"
      />
      <span className="text-sm font-medium text-gray-700">Include 7.5% VAT</span>
    </label>
  </div>

  {includeTax && (
    <div className="ml-6">
      <label className="flex items-center mb-2">
        <input
          type="radio"
          checked={!taxOnServicesOnly}
          onChange={() => setTaxOnServicesOnly(false)}
          className="mr-2"
        />
        <span className="text-sm text-gray-700">Apply VAT on all items</span>
      </label>
      <label className="flex items-center">
        <input
          type="radio"
          checked={taxOnServicesOnly}
          onChange={() => setTaxOnServicesOnly(true)}
          className="mr-2"
        />
        <span className="text-sm text-gray-700">Apply VAT on services/labour only</span>
      </label>
    </div>
  )}
</div>

<div className="flex justify-between mt-4">
        <button
          className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded print:hidden"
          onClick={handleSave}
        >
          Save Update
        </button>

        <button onClick={handlePrintPreview}   disabled={!isSaved}  
       className={`bg-green-600 hover:bg-green-700 text-white font-bold print:hidden
       py-2 px-4 rounded ${!isSaved && 'opacity-50 cursor-not-allowed'}`} >
        Print Preview
      </button>


       
      </div>


      {isModalOpen && <PrintableQuotation quotation={quotation} totals={totals} onClose={handleCloseModal} />}




<ToastContainer />
    </div>
    </>
  );
  
};

export default EditQuotation;
